import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  currentOrganizationSelector,
  organizationUsersSelector,
  userSelector,
} from "../../store/selectors";
import { ReactComponent as MicIcon } from "../../assets/mic.svg";
import { ReactComponent as StopRecordIcon } from "../../assets/StopRecordIcon.svg";
import UserSuggestionDropdown from "./UserSuggestionDropdown";
import TagsSuggestionDropdown from "../TagsSuggestionDropdown/TagsSuggestionDropdown";
import "./WriteComment.css";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import locationIcon from "../../assets/location.svg";
import screenshotIcon from "../../assets/screenshot.svg";
import drawingIcon from "../../assets/drawing.svg";
import attachmentIcon from "../../assets/attachment.svg";
import {
  completeMediaUpload,
  createMediaPresign,
  createTag,
} from "../../store/projectsSlice";
import toast from "react-hot-toast";
import TagComponent from "../TagsSuggestionDropdown/TagComponent";
import TagColorPickerModal from "../TagColorPickerModal/TagColorPickerModal";
import Avatar from "../Avatar/Avatar";
import { OrbitProgress } from "react-loading-indicators";
import EmojiPicker from "emoji-picker-react";
import useIsMobile from "../../hooks/useIsMobile";
import VoiceRecorder from "./VoiceRecorder/VoiceRecorder";
import {
  readDraft,
  writeDraft,
  clearDraft,
  isDraftEmpty,
} from "../../helpers/commentStorage";
import {
  ACCEPT_ATTR,
  ATTACH_LIMITS,
  classifyFile,
  formatBytes,
  uploadAttachment,
} from "./attachmentUpload";
import {
  drawingSizeBytes,
  isEmptyDrawing,
  MAX_DRAWING_BYTES,
} from "../FrameAnnotations/frameGeometry";
import { tagColors } from "../../helpers/tagColors";
import ParsedCommentsModal from "../ParsedCommentsModal/ParsedCommentsModal";
import {
  extractTagNames,
  parseCommentsFromText,
  stripTagNames,
} from "../../helpers/commentParser";

const WriteComment = ({
  newCommentEnd = null,
  setNewCommentEnd = () => {},
  sharedOrgId,
  newMarkerInfo,
  setNewMarkerInfo,
  currentTime,
  versionId,
  onSubmit,
  videoRef,
  isSendingComment,
  videoControls,
  markerPoint,
  onSetMarkerPoint,
  isLocationMode,
  setIsLocationMode,
  draftDrawing,
  onSetDraftDrawing = () => {},
  isDrawingMode = false,
  setIsDrawingMode = () => {},
  onSubmitComment,
  updateTagsArray,
  tagsArray,
  projectUsers,
  tagsDefaultColors,
  currentEditedComment,
  resetCurrentEditedComment,
  isShared,
  onShowSharedNeedLogin,
  onSubmitCommentsList = () => {},
  onTagColorChange,
  onTagColorConfigure,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [isDraftHydrated, setIsDraftHydrated] = useState(false);
  const [isDraftRestored, setIsDraftRestored] = useState(false);
  const [parsedComments, setParsedComments] = useState(null);
  const [isSendingParsed, setIsSendingParsed] = useState(false);
  const forceSingleRef = useRef(false);
  const declinedParsedTextRef = useRef(null);
  const [tagColorModal, setTagColorModal] = useState(null);
  const draftTagIdsRef = useRef(null);
  const draftMentionIdRef = useRef(null);
  const dispatch = useDispatch();
  const [timeRange, setTimeRange] = useState({
    start: currentTime || 0,
    end: null,
  });
  const user = useSelector(userSelector);
  const organizationUsers = useSelector(organizationUsersSelector);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [selectedTagIndex, setSelectedTagIndex] = useState(0);
  const [currentSelectedTags, setCurrentSelectedTags] = useState([]);
  const [currentSelectedUser, setCurrentSelectedUser] = useState(null);
  const [tagCreating, setTagCreating] = useState(false);
  const textareaRef = useRef(null);
  const tags = [
    "Монтаж",
    "Диктор",
    "ГЗГ",
    "Графика",
    "Хроника",
    "Стоки",
    "Контент",
    "Музыка",
  ];
  const colorsArray = tagColors;
  const [currentTagText, setCurrentTagText] = useState("");
  const [currentTagColor, setCurrentTagColor] = useState("#F3A1F0");
  const [emojiPickerOpened, setEmojiPickerOpened] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);

  const [currentRecord, setCurrentRecord] = useState(null);
  const [currentRecordBlob, setCurrentRecordBlob] = useState(null);
  const [voiceMode, setVoiceMode] = useState("idle");
  const voiceRecorderRef = useRef(null);
  const isMobile = useIsMobile();
  const emojiRef = useRef();
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const hasDrawing = !isEmptyDrawing(draftDrawing);

  useEffect(() => {
    if ("virtualKeyboard" in navigator) {
      const geometryChange = (event) => {
        const { height } = event.target.geometry;
        document.documentElement.style.setProperty(
          "--keyboard-height",
          `${height}px`
        );
      };
      navigator.virtualKeyboard.addEventListener(
        "geometrychange",
        geometryChange
      );

      return () => {
        navigator.virtualKeyboard.removeEventListener(
          "geometrychange",
          geometryChange
        );
      };
    }
  }, []);

  useEffect(() => {
    if (!versionId) return;
    const draft = readDraft(versionId);
    if (draft && !isDraftEmpty(draft)) {
      if (draft.text) setComment(draft.text);
      if (draft.tagIds && draft.tagIds.length) {
        draftTagIdsRef.current = draft.tagIds;
      }
      if (draft.mentionUserId) {
        draftMentionIdRef.current = draft.mentionUserId;
      }
      if (draft.timecode) {
        setTimeRange({ start: draft.timecode, end: draft.timecodeEnd || null });
      }
      if (draft.newMarkerInfo) setNewMarkerInfo(draft.newMarkerInfo);
      setIsDraftRestored(true);
    }
    setIsDraftHydrated(true);
  }, [versionId]);

  useEffect(() => {
    const ids = draftTagIdsRef.current;
    if (!ids || !tagsArray || !tagsArray.length) return;
    const restoredTags = tagsArray.filter((tag) => ids.includes(tag.id));
    if (restoredTags.length) setCurrentSelectedTags(restoredTags);
    draftTagIdsRef.current = null;
  }, [tagsArray]);

  useEffect(() => {
    const id = draftMentionIdRef.current;
    if (!id || !projectUsers || !projectUsers.length) return;
    const restoredUser = projectUsers.find((u) => (u.userId || u.id) === id);
    if (restoredUser) setCurrentSelectedUser(restoredUser);
    draftMentionIdRef.current = null;
  }, [projectUsers]);

  useEffect(() => {
    if (!versionId || !isDraftHydrated) return undefined;
    if (currentEditedComment && !currentEditedComment.isPendingRestore) {
      return undefined;
    }

    const tagIds = (currentSelectedTags || []).map((tag) => tag.id);
    if (
      isDraftEmpty({
        text: comment,
        tagIds,
        timecode: timeRange.start,
        newMarkerInfo,
      })
    ) {
      clearDraft(versionId);
      return undefined;
    }

    const timer = setTimeout(() => {
      writeDraft(versionId, {
        text: comment,
        timecode: timeRange.start,
        timecodeEnd: timeRange.end,
        tagIds,
        mentionUserId: currentSelectedUser ? currentSelectedUser.id : null,
        newMarkerInfo: newMarkerInfo || null,
        editingCommentId: null,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [
    comment,
    currentSelectedTags,
    currentSelectedUser,
    timeRange,
    newMarkerInfo,
    isDraftHydrated,
    versionId,
  ]);

  useEffect(() => {
    if (!isDraftRestored) return undefined;
    const timer = setTimeout(() => setIsDraftRestored(false), 3000);
    return () => clearTimeout(timer);
  }, [isDraftRestored]);

  useEffect(() => {
    const handleStartCommenting = (e) => {
      const target = e.target;
      const isEditable =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;
      if (isEditable) return;
      console.log(e);
      if (!e.metaKey && e.code === "KeyC") {
        e.preventDefault();
        textareaRef.current?.focus();
        videoControls.pause();
      }
    };
    window.addEventListener("keydown", handleStartCommenting);
    return () => window.removeEventListener("keydown", handleStartCommenting);
  }, [videoControls]);

  useEffect(() => {
    if (newCommentEnd) {
      setTimeRange(newCommentEnd);
    }
  }, [newCommentEnd]);

  useEffect(() => {
    if (currentEditedComment) {
      setComment(currentEditedComment.content);
      setCurrentSelectedTags(currentEditedComment.tags);
      if (
        currentEditedComment.mentions &&
        currentEditedComment.mentions.length
      ) {
        setCurrentSelectedUser(currentEditedComment.mentions[0]);
      }
      clearPendingFiles();
      setPendingFiles(
        (currentEditedComment.attachments || []).map((a) => ({
          key: `m${a.mediaId}`,
          mediaId: a.mediaId,
          kind:
            a.type === "IMAGE"
              ? "image"
              : a.type === "VOICE"
                ? "voice"
                : "file",
          name:
            a.fileName ||
            (a.type === "VOICE" ? "Голосовое" : `Файл ${a.mediaId}`),
          size: a.sizeBytes,
          previewUrl: a.cdnUrl,
        }))
      );
      onSetMarkerPoint(currentEditedComment.markerPoint || null);
      onSetDraftDrawing(currentEditedComment.drawing || null);
      setIsLocationMode(false);
      setIsDrawingMode(false);
    }
  }, [currentEditedComment]);

  const clearPendingFiles = () => {
    setPendingFiles((prev) => {
      prev.forEach((f) => {
        if (f.file && f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      return [];
    });
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPendingFiles((prev) => {
      const next = [...prev];
      for (const file of files) {
        if (next.length >= ATTACH_LIMITS.maxFiles) {
          toast.error(
            `Не больше ${ATTACH_LIMITS.maxFiles} файлов в одном комментарии`
          );
          break;
        }
        const info = classifyFile(file);
        if (info.error) {
          toast.error(info.error);
          continue;
        }
        next.push({
          key: `f${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          file,
          kind: info.kind,
          contentType: info.contentType,
          name: file.name,
          size: file.size,
          previewUrl: info.kind === "image" ? URL.createObjectURL(file) : null,
        });
      }
      return next;
    });
  };

  const removePendingFile = (key) => {
    setPendingFiles((prev) => {
      const f = prev.find((x) => x.key === key);
      if (f && f.file && f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((x) => x.key !== key);
    });
  };

  const openFilePicker = () => {
    if (isShared) {
      onPressIfShared({ target: { blur: () => {} } });
      return;
    }
    fileInputRef.current?.click();
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const hasEndTime = timeRange.end !== null;

  useEffect(() => {
    if (timeRange.end === null) {
      setTimeRange((prev) => ({
        ...prev,
        start: currentTime || 0,
      }));
    }
  }, [currentTime]);

  const handleLocationClick = () => {
    if (isShared) {
      onPressIfShared({ target: { blur: () => {} } });
      return;
    }
    if (!isLocationMode) {
      setIsDrawingMode(false);
      setIsLocationMode(true);
      if (videoControls?.pause) {
        videoControls.pause();
      }
    } else {
      setIsLocationMode(false);
      onSetMarkerPoint(null);
    }
  };

  const handleDrawingClick = () => {
    if (isShared) {
      onPressIfShared({ target: { blur: () => {} } });
      return;
    }
    if (!isDrawingMode) {
      setIsLocationMode(false);
      setIsDrawingMode(true);
      if (videoControls?.pause) {
        videoControls.pause();
      }
    } else {
      setIsDrawingMode(false);
    }
  };

  useEffect(() => {
    return () => {
      setIsLocationMode(false);
      onSetMarkerPoint(null);
      setIsDrawingMode(false);
      onSetDraftDrawing(null);
    };
  }, []);

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "00:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  const handleAddEndTime = () => {
    const currentVideoTime = videoRef?.current?.currentTime || currentTime || 0;
    setTimeRange((prev) => ({
      ...prev,
      end: currentVideoTime,
    }));
  };

  const handleRemoveEndTime = () => {
    setNewMarkerInfo({
      ...newMarkerInfo,
      positionEnd: null,
      timecodeEnd: null,
    });
    setTimeRange((prev) => ({
      start: currentTime || 0,
      end: null,
    }));
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setComment(value);
    declinedParsedTextRef.current = null;

    const lastAtIndex = value.lastIndexOf("@");
    if (lastAtIndex !== -1) {
      const searchText = value.slice(lastAtIndex + 1);
      const filteredUsers = projectUsers.filter((user) =>
        user.email.toLowerCase().includes(searchText.toLowerCase())
      );

      if (filteredUsers.length > 0) {
        setSuggestedUsers(filteredUsers);
        setShowUserSuggestions(true);
        setSelectedUserIndex(0);
      } else {
        setShowUserSuggestions(false);
      }
    } else {
      setShowUserSuggestions(false);
    }

    const hashMatch = value.match(/(^|\s)#([\wа-яА-Яё]*)$/);
    if (hashMatch) {
      const searchText = hashMatch[2];
      const filteredTags = tags.filter((tag) =>
        tag.toLowerCase().includes(searchText.toLowerCase())
      );
      setSuggestedTags(filteredTags);
      setShowTagSuggestions(true);
      setSelectedTagIndex(0);
      setCurrentTagText(searchText);
    } else {
      setShowTagSuggestions(false);
      setCurrentTagText("");
    }
  };

  const setTagAndSubmit = () => {
    if (tryOpenParsedComments(comment)) return;
    const existingTag = tagsArray.find(
      (tag) => tag.name === currentTagText.toLowerCase()
    );
    if (existingTag) {
      handleTagSelect(existingTag);
      const lastHashIndex = comment.lastIndexOf("#");
      const newComment = comment.slice(0, lastHashIndex);
      handleSubmit(existingTag, newComment);
    } else {
      setTagCreating(true);
      const randomColor =
        colorsArray[Math.floor(Math.random() * colorsArray.length)];
      const tagInfo = {
        name: currentTagText.toLowerCase(),
        color: randomColor,
        organizationId: sharedOrgId || currentOrganization.id,
      };
      dispatch(createTag(tagInfo)).then((res) => {
        if (!res.error) {
          toast.success("Тэг успешно добавлен!");
          setTagCreating(false);
          const lastHashIndex = comment.lastIndexOf("#");
          const newComment = comment.slice(0, lastHashIndex);
          setComment(newComment);
          updateTagsArray();
          setCurrentSelectedTags([...currentSelectedTags, res.payload]);
          setShowTagSuggestions(false);
          handleSubmit(res.payload, newComment);
        } else {
          toast.error("Ошибка при добавлении тэга");
          setTagCreating(false);
        }
      });
    }
  };

  const handleKeyDown = (e) => {
    if (showUserSuggestions) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedUserIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedUserIndex((prev) =>
          prev < suggestedUsers.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleUserSelect(suggestedUsers[selectedUserIndex]);
      } else if (e.key === "Escape") {
        setShowUserSuggestions(false);
      }
    } else if (showTagSuggestions) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedTagIndex((prev) =>
          prev < suggestedTags.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedTagIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        setTagAndSubmit();
      } else if (e.key === "Escape") {
        setShowTagSuggestions(false);
      } else if (e.key === " ") {
        setShowTagSuggestions(false);
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleUserSelect = (user) => {
    const lastAtIndex = comment.lastIndexOf("@");
    const newComment = comment.slice(0, lastAtIndex);
    setComment(newComment);
    setCurrentSelectedUser(user);
    setShowUserSuggestions(false);
  };

  const handleTagSelect = (tag) => {
    const lastHashIndex = comment.lastIndexOf("#");
    let newComment = comment;
    if (lastHashIndex > -1) {
      newComment = comment.slice(0, lastHashIndex);
    }

    setComment(newComment);
    setCurrentSelectedTags([...currentSelectedTags, tag]);
    setShowTagSuggestions(false);
  };

  const rememberLastUsedTag = (tagId) => {
    const lastUsedTags =
      JSON.parse(localStorage.getItem("lastUsedTagIds")) || [];
    const newLastUsedTags = lastUsedTags.filter((id) => id !== tagId);
    localStorage.setItem(
      "lastUsedTagIds",
      JSON.stringify([tagId, ...newLastUsedTags])
    );
  };

  const findOrCreateTags = async (names) => {
    const found = [];
    const usedColors = new Set(
      tagsArray.map((tag) => tag.color).filter(Boolean)
    );
    const availableColors = usedColors.size
      ? colorsArray.filter((c) => !usedColors.has(c))
      : colorsArray;

    for (const tagName of names) {
      const existingTag = tagsArray.find(
        (tagItem) => tagItem.name === tagName.toLowerCase()
      );
      if (existingTag) {
        found.push(existingTag);
        continue;
      }

      const randomColor =
        availableColors[Math.floor(Math.random() * availableColors.length)];
      const res = await dispatch(
        createTag({
          name: tagName.toLowerCase(),
          color: randomColor,
          organizationId: sharedOrgId || currentOrganization.id,
        })
      );
      if (!res.error) {
        found.push(res.payload);
        usedColors.add(randomColor);
      }
    }

    return found;
  };

  const resolveInlineTags = async (text) => {
    const names = extractTagNames(text);
    const found = await findOrCreateTags(names);

    return {
      content: stripTagNames(text),
      tags: [...currentSelectedTags, ...found],
    };
  };

  const tryOpenParsedComments = (text) => {
    if (declinedParsedTextRef.current === text) return false;

    const duration = videoControls?.getDuration?.() || 0;

    const parsed = parseCommentsFromText(text, duration);
    if (!parsed.comments.length) return false;

    setShowTagSuggestions(false);
    setShowUserSuggestions(false);
    setParsedComments(parsed);
    return true;
  };

  // const resolveInlineTags = async (text) => {
  //   const tagRegex = /#([\wa-zA-Zа-яА-Яё]+)/g;
  //   const names = [];
  //   let match;
  //   while ((match = tagRegex.exec(text)) !== null) {
  //     names.push(match[1]);
  //   }

  //   let newSelectedTags = [...currentSelectedTags];
  //   for (const tagName of names) {
  //     const existingTag = tagsArray.find(
  //       (tagItem) => tagItem.name === tagName.toLowerCase()
  //     );
  //     if (existingTag) {
  //       newSelectedTags = [...newSelectedTags, existingTag];
  //     } else {
  //       const randomColor =
  //         colorsArray[Math.floor(Math.random() * colorsArray.length)];
  //       const res = await dispatch(
  //         createTag({
  //           name: tagName.toLowerCase(),
  //           color: randomColor,
  //           organizationId: sharedOrgId || currentOrganization.id,
  //         })
  //       );
  //       if (!res.error) newSelectedTags = [...newSelectedTags, res.payload];
  //     }
  //   }

  //   let cleaned = text;
  //   names.forEach((tagName) => {
  //     cleaned = cleaned.replace(new RegExp(`#${tagName}\\s*`, "g"), "");
  //   });
  //   cleaned = cleaned.replace(/\s+/g, " ").trim();
  //   return { content: cleaned, tags: newSelectedTags };
  // };

  const uploadAllAttachments = async () => {
    const attachments = [];
    let order = 0;
    for (const f of pendingFiles) {
      if (f.mediaId) {
        attachments.push({ mediaId: f.mediaId, sortOrder: order++ });
        continue;
      }
      const mediaId = await uploadAttachment(dispatch, {
        file: f.file,
        kind: f.kind,
        contentType: f.contentType,
        versionId,
      });
      attachments.push({ mediaId, sortOrder: order++ });
    }

    if (currentRecord && currentRecordBlob) {
      const presignRes = await dispatch(
        createMediaPresign({
          mediaData: {
            type: "VOICE",
            contentType: "audio/mp3",
            contextKind: "VIDEO",
            contextId: +versionId,
          },
        })
      );
      if (presignRes.error) throw new Error("Не удалось загрузить голосовое");
      const fetchRes = await fetch(presignRes.payload.uploadUrl, {
        method: "PUT",
        headers: presignRes.payload.requiredHeaders,
        body: currentRecordBlob,
      });
      if (!fetchRes.ok) throw new Error("Не удалось загрузить голосовое");
      const completeRes = await dispatch(
        completeMediaUpload({
          mediaId: presignRes.payload.mediaId,
          mediaData: { durationMs: Math.round((recordDuration || 0) * 1000) },
        })
      );
      if (completeRes.error) throw new Error("Голосовое не принято сервером");
      attachments.push({
        mediaId: presignRes.payload.mediaId,
        sortOrder: order++,
      });
    }
    return attachments;
  };

  const resetComposer = () => {
    clearDraft(versionId);
    setIsDraftRestored(false);
    setNewMarkerInfo(null);
    setComment("");
    setTimeRange({ start: null, end: null });
    if (currentRecord && currentRecord.startsWith("blob:")) {
      URL.revokeObjectURL(currentRecord);
    }
    setCurrentRecord(null);
    setCurrentRecordBlob(null);
    clearPendingFiles();
    setCurrentSelectedTags([]);
    setCurrentSelectedUser(null);
    onSetMarkerPoint(null);
    setIsLocationMode(false);
    onSetDraftDrawing(null);
    setIsDrawingMode(false);
    updateTagsArray();
    resetCurrentEditedComment();
  };

  const canSend =
    !!comment.trim() ||
    (currentSelectedTags && currentSelectedTags.length > 0) ||
    !!currentRecord ||
    pendingFiles.length > 0 ||
    hasDrawing;

  const handleSubmit = async (chosenTag = null, readyCommentText = null) => {
    const commentText = readyCommentText || comment;
    const sendable =
      !!commentText.trim() ||
      (currentSelectedTags && currentSelectedTags.length > 0) ||
      !!currentRecord ||
      pendingFiles.length > 0 ||
      hasDrawing;
    if (!sendable || isUploading || isSendingComment) return;
    if (
      !forceSingleRef.current &&
      !chosenTag &&
      !readyCommentText &&
      tryOpenParsedComments(commentText)
    ) {
      return;
    }
    forceSingleRef.current = false;

    if (hasDrawing && drawingSizeBytes(draftDrawing) > MAX_DRAWING_BYTES) {
      toast.error("Рисунок слишком большой — уберите часть штрихов");
      return;
    }

    let content = commentText;
    let tagIds = [];
    if (chosenTag && chosenTag.id) {
      tagIds = [chosenTag.id];
      content = commentText.slice(0, commentText.lastIndexOf("#"));
      rememberLastUsedTag(chosenTag.id);
    } else if (commentText.indexOf("#") > -1) {
      const resolved = await resolveInlineTags(commentText);
      content = resolved.content;
      tagIds = resolved.tags.map((tag) => tag.id);
      if (resolved.tags.length) rememberLastUsedTag(resolved.tags[0].id);
    } else if (currentSelectedTags.length) {
      tagIds = currentSelectedTags.map((tag) => tag.id);
      rememberLastUsedTag(currentSelectedTags[0].id);
    }

    let attachments = [];
    if (pendingFiles.length || currentRecord) {
      setIsUploading(true);
      try {
        attachments = await uploadAllAttachments();
      } catch (e) {
        toast.error(e.message || "Не удалось загрузить файл");
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    const startSeconds = Number.isFinite(+timeRange.start)
      ? +timeRange.start
      : 0;
    const endSeconds =
      timeRange.end !== null && Number.isFinite(+timeRange.end)
        ? +timeRange.end
        : null;

    const timecode =
      endSeconds !== null ? `${startSeconds} - ${endSeconds}` : startSeconds;

    let commentData = {
      content,
      authorId: user.userId,
      timecode,
      attachments,
      tagIds,
      mentions: currentSelectedUser
        ? [currentSelectedUser.userId || currentSelectedUser.id]
        : [],
    };

    if (markerPoint) {
      commentData = {
        ...commentData,
        markerPoint: markerPoint
          ? { x: +markerPoint.x, y: +markerPoint.y }
          : null,
      };
    }

    if (hasDrawing) {
      commentData = {
        ...commentData,
        drawing: hasDrawing ? draftDrawing : null,
      };
    }

    const isEdit =
      !!currentEditedComment && !currentEditedComment.isPendingRestore;
    if (isEdit) {
      commentData = {
        ...currentEditedComment,
        ...commentData,
      };
    } else if (currentEditedComment?.isPendingRestore) {
      commentData = {
        ...commentData,
        replacePendingId: currentEditedComment.pendingId || null,
      };
    }

    if (isEdit) {
      const res = await onSubmitComment(commentData, true);
      if (res && res.error) return;
    } else {
      onSubmitComment(commentData, false);
    }

    resetComposer();
  };

  const handleConfirmParsedComments = async () => {
    if (!parsedComments) return;

    const { comments: parsed } = parsedComments;
    setParsedComments(null);
    setIsSendingParsed(true);

    try {
      let attachments = [];
      if (pendingFiles.length || currentRecord) {
        try {
          attachments = await uploadAllAttachments();
        } catch (e) {
          toast.error(e.message || "Не удалось загрузить файл");
        }
      }

      const payload = [];

      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        const names = extractTagNames(item.body);
        const tags = await findOrCreateTags(names);
        const isFirst = i === 0;

        const commentData = {
          content: stripTagNames(item.body),
          authorId: user.userId,
          timecode: item.seconds,
          attachments: isFirst ? attachments : [],
          tagIds: tags.map((tag) => tag.id),
          mentions: [],
        };

        if (isFirst && markerPoint) {
          commentData.markerPoint = { x: +markerPoint.x, y: +markerPoint.y };
        }
        if (isFirst && hasDrawing) {
          commentData.drawing = draftDrawing;
        }

        payload.push(commentData);
      }

      await onSubmitCommentsList(payload);
    } finally {
      setIsSendingParsed(false);
      resetComposer();
    }
  };

  const handleSendParsedAsSingle = () => {
    setParsedComments(null);
    forceSingleRef.current = true;
    handleSubmit();
  };

  const handleCloseParsedComments = () => {
    declinedParsedTextRef.current = comment;
    setParsedComments(null);
  };

  const handleTagColorChange = (tagId, color) => {
    if (onTagColorChange) onTagColorChange(tagId, color);
    setTagColorModal(null);
  };

  const handleTagColorConfigure = (tag) => {
    setTagColorModal({
      tag,
      color: tagsDefaultColors[tag.id] || tag.color || "#9D7BFF",
    });
  };

  const onPressIfShared = (e) => {
    e.target.blur();
    onShowSharedNeedLogin();
  };

  const handleCreateTag = () => {
    setTagCreating(true);
    const randomColor =
      colorsArray[Math.floor(Math.random() * colorsArray.length)];
    const tagInfo = {
      name: currentTagText.toLowerCase(),
      color: randomColor,
      organizationId: sharedOrgId || currentOrganization.id,
    };
    dispatch(createTag(tagInfo)).then((res) => {
      if (!res.error) {
        toast.success("Тэг успешно добавлен!");
        setTagCreating(false);
        const lastHashIndex = comment.lastIndexOf("#");
        const newComment = comment.slice(0, lastHashIndex);
        setComment(newComment);
        updateTagsArray();
        setCurrentSelectedTags([...currentSelectedTags, res.payload]);
        setShowTagSuggestions(false);
      } else {
        toast.error("Ошибка при добавлении тэга");
        setTagCreating(false);
      }
    });
  };

  const handleMicButtonClick = () => {
    if (isShared) {
      onPressIfShared();
      return;
    }
    if (voiceMode === "recording") {
      voiceRecorderRef.current?.stopRecording();
      return;
    }
    if (voiceMode === "recorded") {
      voiceRecorderRef.current?.restartRecording();
      return;
    }
    voiceRecorderRef.current?.startRecording();
  };

  return (
    <div className={isMobile ? "write-comment-mobile" : "write-comment"}>
      {currentEditedComment && (
        <div
          style={{
            width: "100%",
            backgroundColor: "#181c24",
            bottom: 0,
            zIndex: 1,
            borderRadius: 16,
            paddingTop: 10,
            paddingLeft: 15,
            paddingRight: 10,
            paddingBottom: 10,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 500, color: "white" }}>
              Редактирование
            </span>
            <span
              className="edited-comment-text"
              style={{ fontSize: 14, fontWeight: 500, color: "#717b8e" }}
            >
              {currentEditedComment.content}
            </span>
          </div>
          <button
            style={{
              width: "24px",
              height: "24px",
              background: "none",
              border: "none",
              color: "#c1c4cb",
              fontSize: "24px",
              cursor: "pointer",
            }}
            onClick={() => {
              resetCurrentEditedComment();
              setComment("");
              clearPendingFiles();
              onSetMarkerPoint(null);
              onSetDraftDrawing(null);
              setIsLocationMode(false);
              setIsDrawingMode(false);
            }}
          >
            &times;
          </button>
        </div>
      )}
      <div className="write-comment-main-content">
        <div className={`timestamp-container ${hasEndTime ? "has-range" : ""}`}>
          <button className="timestamp">{formatTime(timeRange.start)}</button>
          {(!hasEndTime && (
            <button
              className="add-timestamp"
              onClick={isShared ? onPressIfShared : handleAddEndTime}
            >
              +
            </button>
          )) ||
            null}
          {(hasEndTime && (
            <>
              {/* <div className="timestamp-line" /> */}
              <button className="timestamp second-timestamp">
                {formatTime(timeRange.end)}
                <button
                  className="remove-timestamp"
                  onClick={handleRemoveEndTime}
                />
              </button>
            </>
          )) ||
            null}
          <button
            className="add-timestamp"
            style={{ fontSize: 15 }}
            onClick={() => setShowTagSuggestions((v) => !v)}
          >
            #
          </button>
          {(currentSelectedTags && currentSelectedTags.length && (
            <div
              className="new-tags-row"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 3,
                overflowX: "scroll",
                flex: 1,
              }}
            >
              {currentSelectedTags.map((tagValue) => (
                <div style={{ position: "relative" }}>
                  <TagComponent
                    tagsDefaultColors={tagsDefaultColors}
                    key={tagValue.id}
                    tag={tagValue}
                    onClick={() => {
                      const newCurrentSelectedTags = currentSelectedTags.filter(
                        (tagVal) => tagVal.id !== tagValue.id
                      );
                      setCurrentSelectedTags(newCurrentSelectedTags);
                    }}
                  />
                  <div
                    onClick={() => {
                      const newCurrentSelectedTags = currentSelectedTags.filter(
                        (tagVal) => tagVal.id !== tagValue.id
                      );
                      setCurrentSelectedTags(newCurrentSelectedTags);
                    }}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: -5,
                      width: 15,
                      height: 15,
                      border: "0px solid white",
                      backgroundColor: "rgba(31, 36, 46, 1)",
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      boxSizing: "border-box",
                      paddingBottom: 3,
                      fontSize: 10,
                      cursor: "pointer",
                    }}
                  >
                    x
                  </div>
                </div>
              ))}
            </div>
          )) ||
            null}
          {(currentSelectedUser && (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => setCurrentSelectedUser(null)}
            >
              <Avatar name={currentSelectedUser.email} size={24} />
            </div>
          )) ||
            null}
        </div>

        <div className="comment-input-container">
          {pendingFiles.length > 0 && (
            <div className="pending-files">
              {pendingFiles.map((f) => (
                <div
                  key={f.key}
                  className={`pending-file pending-file--${f.kind}`}
                  title={f.name}
                >
                  {f.kind === "image" && f.previewUrl ? (
                    <img
                      src={f.previewUrl}
                      alt={f.name}
                      className="pending-file-thumb"
                    />
                  ) : (
                    <>
                      <img
                        src={attachmentIcon}
                        alt=""
                        className="pending-file-icon"
                      />
                      <span className="pending-file-name">{f.name}</span>
                      {f.size ? (
                        <span className="pending-file-size">
                          {formatBytes(f.size)}
                        </span>
                      ) : null}
                    </>
                  )}
                  <button
                    type="button"
                    className="pending-file-remove"
                    onClick={() => removePendingFile(f.key)}
                    title={t("comments.attach.remove")}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div
            style={{
              display: voiceMode === "idle" ? "none" : "block",
              marginBottom: voiceMode === "recording" ? 0 : 8,
            }}
          >
            <VoiceRecorder
              ref={voiceRecorderRef}
              recordUrl={currentRecord}
              isShared={isShared}
              onModeChange={setVoiceMode}
              onRecorded={(blob, blobUrl) => {
                setCurrentRecord(blobUrl);
                setCurrentRecordBlob(blob);
              }}
              onRecordCleared={() => {
                setCurrentRecord(null);
                setCurrentRecordBlob(null);
                setRecordDuration(0);
              }}
              onLimitReached={() =>
                toast("Достигнута максимальная длительность записи — 5 минут")
              }
              onDurationChange={setRecordDuration}
            />
          </div>
          {isDraftRestored && (
            <span className="write-comment-draft-hint">
              Восстановлен черновик
            </span>
          )}
          {voiceMode !== "recording" && (
            <textarea
              ref={textareaRef}
              className="comment-input"
              placeholder={t("comments.write")}
              value={comment}
              onChange={handleCommentChange}
              onKeyDown={handleKeyDown}
              onPaste={isShared ? undefined : handlePaste}
              rows={1}
              onFocus={isShared ? onPressIfShared : () => {}}
            />
          )}
          {showUserSuggestions && (
            <UserSuggestionDropdown
              users={suggestedUsers}
              searchQuery={comment.slice(comment.lastIndexOf("@") + 1)}
              onSelect={handleUserSelect}
              selectedIndex={selectedUserIndex}
            />
          )}
          {showTagSuggestions && (
            <TagsSuggestionDropdown
              onClose={() => setShowTagSuggestions(false)}
              tags={suggestedTags}
              searchQuery={currentTagText}
              onSelect={handleTagSelect}
              selectedIndex={selectedTagIndex}
              handleCreateTag={handleCreateTag}
              tagCreating={tagCreating}
              currentTagColor={currentTagColor}
              tagsArray={tagsArray}
              colorsArray={colorsArray}
              setCurrentTagColor={setCurrentTagColor}
              tagsDefaultColors={tagsDefaultColors}
              updateTagsArray={updateTagsArray}
              onTagColorChange={onTagColorChange}
              onTagColorConfigure={onTagColorConfigure}
            />
          )}
        </div>

        <div className="actions-container">
          <div className="action-buttons">
            <button
              className={`action-button ${
                isLocationMode || markerPoint ? "active" : ""
              }`}
              onClick={handleLocationClick}
              title={
                isLocationMode
                  ? t("comments.point.cancel")
                  : markerPoint
                    ? t("comments.point.replace")
                    : t("comments.point.add")
              }
            >
              <img src={locationIcon} alt="Location" />
            </button>
            <button
              className={`action-button ${
                isDrawingMode || hasDrawing ? "active" : ""
              }`}
              onClick={handleDrawingClick}
              title={
                isDrawingMode
                  ? t("comments.drawing.done")
                  : t("comments.drawing.add")
              }
            >
              <img src={drawingIcon} alt="Drawing" />
            </button>
            <button
              className={`action-button ${pendingFiles.length ? "active" : ""}`}
              onClick={openFilePicker}
              title={t("comments.attach.add")}
            >
              <img src={attachmentIcon} alt="Attach" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              style={{ display: "none" }}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          <div className="right-buttons">
            <ClickAwayListener
              onClickAway={(e) => {
                if (!emojiPickerOpened) return;
                const isInside =
                  (e.target.className &&
                    e.target.className.includes("epr-btn")) ||
                  e.target.className.includes("epr-emoji");
                if (!isInside) {
                  setEmojiPickerOpened(false);
                }
              }}
            >
              <div
                style={{ cursor: "pointer" }}
                onClick={
                  isShared
                    ? onPressIfShared
                    : () => setEmojiPickerOpened(!emojiPickerOpened)
                }
              >
                😃
              </div>
            </ClickAwayListener>
            <EmojiPicker
              searchDisabled={true}
              onEmojiClick={(emoji) => setComment(comment + "" + emoji.emoji)}
              categories={[]}
              previewConfig={{
                showPreview: false,
              }}
              style={{
                position: "absolute",
                bottom: 120,
                zIndex: 10000,
                width: "90%",
                scrollbarWidth: "none" /* Firefox */,
                msOverflowStyle: "none" /* IE and Edge */,
                "&::-webkit-scrollbar": {
                  /* Chrome, Safari and Opera */ display: "none",
                },
              }}
              theme="dark"
              ref={emojiRef}
              open={emojiPickerOpened}
            />
            <button
              onClick={handleMicButtonClick}
              title={
                voiceMode === "recording"
                  ? "Остановить запись"
                  : voiceMode === "recorded"
                    ? "Записать заново"
                    : "Записать голосовое"
              }
              className={`action-button ${
                voiceMode === "recording" ? "recording" : ""
              }`}
            >
              {voiceMode === "recording" ? (
                <StopRecordIcon
                  style={{
                    stroke: "#9a6aff",
                    color: "#9a6aff",
                    transform: "scale(0.8)",
                  }}
                />
              ) : (
                <MicIcon
                  style={{
                    stroke: "#9a6aff",
                    color: "#9a6aff",
                    transform: "scale(0.8)",
                  }}
                />
              )}
            </button>
            <button
              className="send-button"
              onClick={() => handleSubmit()}
              disabled={
                isUploading ||
                isSendingParsed ||
                (!canSend && !isSendingComment)
              }
            >
              {isSendingComment || isSendingParsed || isUploading ? (
                <OrbitProgress style={{ fontSize: 4 }} color="white" />
              ) : currentEditedComment ? (
                "Сохранить"
              ) : (
                t("comments.send")
              )}
            </button>
            {/* <button
              className="send-button"
              onClick={() => handleSubmit()}
              disabled={isUploading || (!canSend && !isSendingComment)}
            >
              {isSendingComment || isUploading ? (
                <OrbitProgress style={{ fontSize: 4 }} color="white" />
              ) : currentEditedComment ? (
                "Сохранить"
              ) : (
                t("comments.send")
              )}
            </button> */}
          </div>
        </div>
      </div>
      {parsedComments && (
        <ParsedCommentsModal
          isOpen
          comments={parsedComments.comments}
          leftover={parsedComments.leftover}
          isSending={isSendingParsed}
          onConfirm={handleConfirmParsedComments}
          onSendAsOne={handleSendParsedAsSingle}
          onClose={handleCloseParsedComments}
        />
      )}
      {tagColorModal && (
        <TagColorPickerModal
          isOpen
          tag={tagColorModal.tag}
          currentColor={tagColorModal.color}
          onSave={handleTagColorChange}
          onClose={() => setTagColorModal(null)}
        />
      )}
    </div>
  );
};

export default WriteComment;
